import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Flex, Grid, Spinner } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import SelectInput from "components/forms/SelectInput";
import SearchField from "components/forms/SearchField";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";
import { mnt, mntType, mntStatCard } from "theme/maintenanceTokens";
import {
  SourcePill,
  MapStatusPill,
  showsMapStatus,
  UnresolvedPill,
  UrgentPill,
  OneOffPill,
  lifecycleBadge,
  slaCell,
  receivedCell,
  formatNte,
  isPostRfq,
  canMatchWo,
  canGenerateRfqForWo,
  workOrderDisplayIdentity,
  unresolvedReason as woUnresolvedReason,
} from "./components/workOrderPresentation";
import { useToast } from "components/toast";
import UserContext from "context/User";

import {
  getWorkOrders,
  getWorkOrderCounts,
  getPlanGroups,
  assignWorkOrderGroup,
  processWorkOrderIntake,
} from "api/ServicePlans";
import { getClients } from "api/Clients";

import MatchCandidatesModal from "./components/MatchCandidatesModal";
import NewWorkOrderModal from "./components/NewWorkOrderModal";

/**
 * §3.9 #16 UI half — WO Review queue. Design reference: mockup 06
 * (06-wo-review-queue.svg).
 *
 * SESSION 46 mockup-06 density pass (MOCKUP_AUDIT GAP-7/8):
 *   - GAP-7 stat card row: Received today / Unmatched / Ready for RFQ /
 *     RFQs generated this week. The queue's at-a-glance layer. Computed
 *     client-side off an "all" fetch so the numbers are truthful
 *     regardless of the active lifecycle filter.
 *   - GAP-8 columns: Source (CorrigoPro vs Manual), Site (city +
 *     plan on a second line, or the unresolved-reason cue), Service ·
 *     SLA (complete-by with urgency), and NTE. These four are what
 *     let staff triage without opening every row.
 *
 * SESSION 55 fidelity pass against the mockup IMAGE:
 *   - Stat cards are borderless beige and CALM; urgency is carried by
 *     the value color (amber unmatched / green ready), not by tinting
 *     the whole card.
 *   - Source renders as a chip (soft blue CorrigoPro, grey Manual).
 *   - The unresolved cue is an amber pill under the site, and now also
 *     renders for known sites that belong to no plan (it used to be
 *     swallowed by the city/plan line).
 *   - Action weighting per the mockup: filled Generate RFQ for ready
 *     rows, outlined Match for unmatched ones.
 *   - All row presentation (badge, SLA, aging, NTE, chips) comes from
 *     the shared `components/workOrderPresentation` module so a WO reads
 *     identically here, on Plan Detail, and on the WO case file.
 *
 * Consumes:
 *   - GET /maintenance/work-orders (list)
 *   - GET /:id/match-candidates + PATCH /:id/match
 *   - GET /:id/convert-preview + POST /:id/convert
 *
 * OPERATING_MODEL §6.5 (Session 52): no "reject" on a WO — we don't
 * reject the client's own instruction. Reject was removed from this
 * queue; declining work goes through the trunk RFQ/Quote flow. Legacy
 * `rejected` rows still render their badge but offer no reject action.
 * Also §6.5: a WO whose RFQ has been generated (status='converted') is
 * an IN-PROGRESS case, not a finished/archived one — the WO stays the
 * live organizing artifact. Labels and the lifecycle filter reflect
 * that ("In progress", not "Converted"); only legacy `rejected` rows
 * are treated as closed.
 *
 * The list endpoint returns a flat array with flat query params
 * (status/matchStatus/lifecycle/servicePlanId/groupId); a plain fetch +
 * local state mirrors PlansTab/ContactsTab in this module rather than
 * the TableData {items,total,pages} cycle the trunk index pages use.
 */
export default function WorkOrderReviewQueue() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [workOrders, setWorkOrders] = useState<any[] | null>(null);
  // Scale pass 2 (a) — the four stat cards come from a server-side
  // aggregate now, not a full "all work orders" fetch. The header stays
  // honest regardless of the active lifecycle filter (the counts are
  // company-wide by construction) without shipping every WO row to count
  // four numbers — the unbounded read this pass removes.
  const [stats, setStats] = useState({
    receivedToday: 0,
    unmatched: 0,
    ready: 0,
    rfqGeneratedThisWeek: 0,
    cancellationRequests: 0,
  });
  const [loading, setLoading] = useState(true);
  const [groupsByPlan, setGroupsByPlan] = useState<Record<string, any[]>>({});
  const [assignTarget, setAssignTarget] = useState<any>(null);
  const [assignGroupId, setAssignGroupId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  // Session 61 (Jack): default to "open", matching the plan's default and
  // vocabulary. "Actionable/Needs action" (pre-RFQ only) was too narrow a
  // landing state - the main queue looked empty while live post-RFQ cases
  // sat on the plan. "Open" = everything not finished; "Needs action" is
  // now an opt-in narrower lens.
  const [lifecycleFilter, setLifecycleFilter] = useState<string>("open");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  // A2 — three added triage axes, all server-side (the queue is the
  // unbounded surface, so a client-side filter over one page would hide
  // matches on other pages — same reasoning as the source filter).
  //   clientFilter — cross-plan triage ("show me all LCG"). Populated
  //     from the client list, not the visible page.
  //   slaFilter    — complete-by urgency (breached / due soon).
  //   batchFilter  — scoped to the selected client's batches; only
  //     meaningful once a client narrows the set, so the control is
  //     hidden until a client is picked.
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [slaFilter, setSlaFilter] = useState<string>("all");
  const [batchFilter, setBatchFilter] = useState<string>("all");
  const [clientOptions, setClientOptions] = useState<{ value: string; label: string }[]>([]);
  // Scale pass 2 (b/c) — the row list is server-paginated now. `page` is
  // 1-based; `total` is the full filtered count from the paged envelope,
  // so the pager can show "x-y of N" and know when a next page exists.
  // Source narrowing moved server-side (see `load`) so a page holds the
  // right rows: a client-side source filter over one page would hide
  // matches sitting on other pages.
  const [page, setPage] = useState<number>(1);
  // Search (Session 69). Two pieces of state on purpose: `searchInput` is
  // what the box shows and updates per keystroke, `search` is the value
  // the SERVER sees, debounced so a five-letter site name is one query
  // rather than five. Same 500ms TableData uses on Quotes/Invoices; unlike
  // TableData there is no 3-character floor, because a two-digit fragment
  // of a Corrigo WO number is a legitimate thing to type here.
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const PAGE_SIZE = 50;
  const [matchTarget, setMatchTarget] = useState<any>(null);
  /**
   * The open work order, and the two densities that hang off it
   * (Session 82). Same mechanism as the plan roster and the plan
   * work-order list.
   */
  const [openWoId, setOpenWoId] = useState<string | null>(null);
  const openWo = (workOrders || []).find((w: any) => w.id === openWoId) ?? null;

  // Filters change the result set constantly on this screen, and a pane
  // left describing a row that has been filtered away is worse than no
  // pane at all.
  useEffect(() => {
    if (openWoId && !(workOrders || []).some((w: any) => w.id === openWoId)) setOpenWoId(null);
  }, [workOrders, openWoId]);
  const [showNewWo, setShowNewWo] = useState(false);
  // Session 76 — guards the manual intake trigger so a slow mailbox run
  // can't be fired twice and create duplicate processing attempts.
  const [checkingIntake, setCheckingIntake] = useState(false);

  useEffect(() => {
    if (user && user.accessLevel !== "isCompany") {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line
  }, [user?.accessLevel]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Work Order Review Queue";
  }, []);

  const load = async () => {
    setLoading(true);
    const query: any = { page, pageSize: PAGE_SIZE };
    if (lifecycleFilter) query.lifecycle = lifecycleFilter;
    if (sourceFilter && sourceFilter !== "all") query.sourceType = sourceFilter;
    if (search.trim()) query.search = search.trim();
    if (clientFilter && clientFilter !== "all") query.clientId = clientFilter;
    if (slaFilter && slaFilter !== "all") query.slaState = slaFilter;
    // Batch only narrows when a client is selected (the control is hidden
    // otherwise); guard so a stale batch value from a prior client can't
    // filter after the client is cleared.
    if (clientFilter && clientFilter !== "all" && batchFilter && batchFilter !== "all") {
      query.groupId = batchFilter;
    }
    const [res, countsRes] = await Promise.all([
      getWorkOrders({ query }),
      getWorkOrderCounts(), // server-side aggregate for the stat cards
    ]);
    // The list endpoint returns a bare array for un-paged callers and a
    // { items, total, page, pageSize } envelope when `page` is set. The
    // queue always pages, so read the envelope; tolerate a bare array in
    // case an older backend answers.
    if (Array.isArray(res)) {
      setWorkOrders(res);
      setTotal(res.length);
    } else {
      setWorkOrders(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total) || 0);
    }
    setStats(
      countsRes ?? {
        receivedToday: 0,
        unmatched: 0,
        ready: 0,
        rfqGeneratedThisWeek: 0,
        cancellationRequests: 0,
      },
    );
    setLoading(false);
  };

  /**
   * Session 76 — pull the work order mailbox on demand.
   *
   * Reports what actually happened rather than just "done": a run that
   * finds nothing is the common case and needs to read as success, not
   * as a silent failure. Errors are surfaced with their count so a
   * partial run is visible.
   */
  const checkIntake = async () => {
    setCheckingIntake(true);
    const res: any = await processWorkOrderIntake();
    setCheckingIntake(false);

    if (!res || typeof res.processed !== "number") {
      toast.add(
        res?.message || "Could not reach the work order mailbox.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }

    const { processed = 0, skipped = 0, errors = 0 } = res;
    const revised = Array.isArray(res.details)
      ? res.details.filter((d: any) => d?.result === "revised").length
      : 0;

    if (errors > 0) {
      toast.add(
        `Checked the mailbox: ${processed} added, ${errors} failed. See the Lambda log.`,
        "var(--theme-ui-colors-danger)",
      );
    } else if (processed === 0 && revised === 0) {
      toast.add(
        skipped > 0
          ? `No new work orders. ${skipped} already in the queue.`
          : "No new work orders in the mailbox.",
        "var(--theme-ui-colors-primary)",
      );
    } else {
      const bits: string[] = [];
      if (processed > 0) bits.push(`${processed} new work order${processed === 1 ? "" : "s"}`);
      if (revised > 0) bits.push(`${revised} revised`);
      toast.add(`Added ${bits.join(", ")}.`, "var(--theme-ui-colors-success)");
    }

    load();
  };

  // Re-fetch whenever the filters or the page change. Filter changes
  // reset to page 1 (below) so we never land on a page past the end of a
  // freshly-narrowed set.
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [lifecycleFilter, sourceFilter, clientFilter, slaFilter, batchFilter, search, page]);

  // Debounce the box into the value the fetch reads.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A filter change must restart paging at 1. Kept separate from `load`
  // so changing a filter doesn't fetch twice (this resets page, the
  // effect above fetches).
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [lifecycleFilter, sourceFilter, clientFilter, slaFilter, batchFilter, search]);

  // A2 — clearing or changing the client drops any batch selection (a
  // batch belongs to one client's plans; it can't carry across).
  useEffect(() => {
    setBatchFilter("all");
  }, [clientFilter]);

  // A2 — load the client list once for the client filter dropdown.
  // Staff-only surface; the client set is bounded (~hundreds), so a
  // single list fetch is cheap and lets the operator filter by any
  // client, not just those on the current page.
  useEffect(() => {
    let alive = true;
    getClients({ query: {} }).then((res: any) => {
      if (!alive) return;
      const rows = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
      const opts = rows
        .filter((c: any) => c?.id && c?.name)
        .map((c: any) => ({ value: c.id, label: c.name }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
      setClientOptions([{ value: "all", label: "All clients" }, ...opts]);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!workOrders || workOrders.length === 0) return;
    const planIds = Array.from(
      new Set(
        workOrders
          .map((wo: any) => wo?.servicePlan?.id)
          .filter((id: string | undefined): id is string => !!id),
      ),
    );
    const missing = planIds.filter((id) => !(id in groupsByPlan));
    if (missing.length === 0) return;
    Promise.all(
      missing.map(async (planId) => {
        const rows = await getPlanGroups({ data: { planId } });
        return [planId, Array.isArray(rows) ? rows : []] as [string, any[]];
      }),
    ).then((entries) => {
      setGroupsByPlan((prev) => {
        const next = { ...prev };
        for (const [planId, rows] of entries) next[planId] = rows;
        return next;
      });
    });
    // eslint-disable-next-line
  }, [workOrders]);

  // GAP-7 — stat cards now come from GET /maintenance/work-orders/counts
  // (see `load`), a server-side aggregate. The client no longer loops a
  // full WO snapshot to compute them; the bucket definitions live in
  // WorkOrderService.counts and are kept in lockstep with the labels
  // below. `moment` stays imported for the row presentation helpers.
  //
  // Source narrowing and pagination are BOTH server-side now (see
  // `load`), so `workOrders` already holds exactly the rows to render
  // for the current page — no client-side `visibleWos` slice.

  const submitAssign = async () => {
    if (!assignTarget?.id) return;
    setAssigning(true);
    const res = await assignWorkOrderGroup({
      data: { id: assignTarget.id, groupId: assignGroupId },
    });
    setAssigning(false);
    if (res?.id) {
      toast.add(
        assignGroupId ? "Work order assigned to batch." : "Work order removed from batch.",
        "var(--theme-ui-colors-success)",
      );
      setAssignTarget(null);
      setAssignGroupId(null);
      load();
    } else {
      toast.add(
        res?.message || "Error updating batch assignment.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  const lifecycleOptions = [
    { value: "open", label: "Open" },
    { value: "actionable", label: "Needs action" },
    { value: "needs_match", label: "Needs match" },
    { value: "needs_convert", label: "Needs RFQ" },
    { value: "in_progress", label: "In progress" },
    // `terminal` = status 'rejected', the shared terminal for both a
    // cancelled WO (the in-flight teardown) and a rejected intake row.
    // Cancel is the common producer in the maintenance module, so it is
    // labelled Cancelled here; excluded from the default Open view.
    { value: "terminal", label: "Cancelled" },
    { value: "", label: "All" },
  ];

  const sourceOptions = [
    { value: "all", label: "All sources" },
    { value: "corrigo", label: "CorrigoPro" },
    { value: "scheduled", label: "Recurring" },
    { value: "manual", label: "Manual / other" },
  ];

  const slaOptions = [
    { value: "all", label: "Any timing" },
    { value: "breached", label: "Overdue" },
    { value: "due_soon", label: "Due soon (48h)" },
  ];

  // A2 — batch options for the selected client, derived from the batches
  // of the plans present in the loaded rows (which, with a client filter
  // active, are that client's plans). Deduped by id. A batch carrying no
  // WO in the current view is not worth offering, so page-scoping is fine.
  const batchOptions = (() => {
    if (clientFilter === "all") return [];
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [{ value: "all", label: "All batches" }];
    for (const groups of Object.values(groupsByPlan)) {
      for (const g of groups || []) {
        if (g?.id && !seen.has(g.id)) {
          seen.add(g.id);
          opts.push({ value: g.id, label: g.name || "Batch" });
        }
      }
    }
    return opts;
  })();

  return (
    <>
      <Header></Header>
      <Page
        title="Work Order Review Queue"
        subtitle="Inbound work orders awaiting Location match, RFQ generation, or review."
        titleAction={
          <Flex sx={{ gap: "10px", alignItems: "center" }}>
            {/* Session 76 — manual intake trigger. Non-production never
                subscribes a Graph webhook, and production only does so
                once WORKORDER_INTAKE_LIVE is "true", so without this the
                only way to pull inbound work orders is a devtools
                console call. Stays useful after go-live as the "check
                now" when someone asks why a WO hasn't appeared. */}
            <BasicButton
              type="button"
              outline
              scheme="primary"
              disabled={checkingIntake}
              onClick={checkIntake}
            >
              {checkingIntake ? "Checking…" : "Check for new work orders"}
            </BasicButton>
            <BasicButton type="button" scheme="primary" onClick={() => setShowNewWo(true)}>
              + New Work Order
            </BasicButton>
          </Flex>
        }
      >
        <Grid
          columns={stats.cancellationRequests > 0 ? [2, 5] : [2, 4]}
          gap={"14px"}
          sx={{ marginBottom: "20px" }}
        >
          {/* Session 80 — shown ONLY when there is at least one. A
              permanent "0 cancellation requests" card would spend a
              fifth of this header on the rarest thing that happens
              here; appearing is the signal. */}
          {stats.cancellationRequests > 0 && (
            <StatCard
              label="Cancellation requests"
              value={stats.cancellationRequests}
              sub="clients waiting on an answer"
              valueTone="amber"
            />
          )}
          <StatCard label="Received today" value={stats.receivedToday} sub="new intake" />
          <StatCard
            label="Unmatched"
            value={stats.unmatched}
            sub="need Match or Create"
            valueTone={stats.unmatched > 0 ? "amber" : undefined}
          />
          <StatCard
            label="Ready for RFQ"
            value={stats.ready}
            sub="site matched"
            valueTone={stats.ready > 0 ? "green" : undefined}
          />
          <StatCard
            label="RFQs generated this week"
            value={stats.rfqGeneratedThisWeek}
            sub={"\u00A0"}
          />
        </Grid>

        <Flex sx={{ marginBottom: "16px", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ minWidth: "200px" }}>
            <SelectInput
              name="lifecycle"
              value={lifecycleFilter}
              options={lifecycleOptions}
              onChange={(v: any) => setLifecycleFilter(v ?? "open")}
              searchable={false}
              clearable={false}
            />
          </Box>
          <Box sx={{ minWidth: "180px" }}>
            <SelectInput
              name="source"
              value={sourceFilter}
              options={sourceOptions}
              onChange={(v: any) => setSourceFilter(v ?? "all")}
              searchable={false}
              clearable={false}
            />
          </Box>
          <Box sx={{ minWidth: "220px" }}>
            <SelectInput
              name="client"
              value={clientFilter}
              options={clientOptions.length ? clientOptions : [{ value: "all", label: "All clients" }]}
              onChange={(v: any) => setClientFilter(v ?? "all")}
              searchable={clientOptions.length > 8}
              clearable={false}
            />
          </Box>
          <Box sx={{ minWidth: "170px" }}>
            <SelectInput
              name="sla"
              value={slaFilter}
              options={slaOptions}
              onChange={(v: any) => setSlaFilter(v ?? "all")}
              searchable={false}
              clearable={false}
            />
          </Box>
          {/* A2 — batch narrows within a client; shown only once a client
              is selected (a batch has no meaning across all clients). */}
          {clientFilter !== "all" && batchOptions.length > 1 && (
            <Box sx={{ minWidth: "180px" }}>
              <SelectInput
                name="batch"
                value={batchFilter}
                options={batchOptions}
                onChange={(v: any) => setBatchFilter(v ?? "all")}
                searchable={false}
                clearable={false}
              />
            </Box>
          )}
          {/* Search, mirroring the Quotes/Invoices toolbar: same
              SearchField, server-side, debounced. Pushed to the right of
              the filter row so the narrowing controls stay grouped. */}
          <Box sx={{ marginLeft: "auto" }}>
            <SearchField
              name="woSearch"
              value={searchInput}
              width={"250px"}
              onChange={(e: any) => setSearchInput(e?.target?.value ?? "")}
            />
          </Box>
        </Flex>

        <Card>
          {/* The header moved INSIDE the list, because it is now
              conditional: no columns exist to label once a record is
              open. Leaving it here rendered a stale seven-column header
              above a two-column list. */}
          {loading ? (
            <Box sx={{ textAlign: "center", paddingY: "40px" }}>
              <Spinner />
            </Box>
          ) : !workOrders || workOrders.length === 0 ? (
            <Box sx={{ padding: "40px", textAlign: "center" }}>
              <Subtext fontSize={"13px"} fontFamily={"Poppins"}>
                No work orders found for this filter.
              </Subtext>
            </Box>
          ) : (
            <Flex sx={{ gap: "16px", alignItems: "flex-start" }}>
              <Box sx={{ flex: openWo ? "0 0 320px" : "1 1 auto", minWidth: 0 }}>
                {!openWo && <QueueListHeader />}
                {workOrders.map((wo, idx) => (
                  <QueueListRow
                    key={wo.id}
                    wo={wo}
                    isLast={idx === workOrders.length - 1}
                    dense={!!openWo}
                    isOpen={wo.id === openWoId}
                    onOpen={() => setOpenWoId((cur) => (cur === wo.id ? null : wo.id))}
                  />
                ))}
              </Box>

              {openWo && (
                <QueuePane
                  wo={openWo}
                  currentBatchName={(() => {
                    if (!openWo.groupId || !openWo.servicePlan?.id) return null;
                    const groups = groupsByPlan[openWo.servicePlan.id];
                    if (!groups) return null;
                    return groups.find((g: any) => g.id === openWo.groupId)?.name ?? null;
                  })()}
                  onClose={() => setOpenWoId(null)}
                  onOpen={() => navigate(`/maintenance/work-orders/${openWo.id}`)}
                  onMatch={() => setMatchTarget(openWo)}
                  onGenerateRfq={() => navigate(`/maintenance/work-orders/${openWo.id}`)}
                  onAssignBatch={() => {
                    setAssignTarget(openWo);
                    setAssignGroupId(openWo?.groupId ?? null);
                  }}
                  onViewRfq={(rfqId: string) => navigate(`/quote-requests/${rfqId}`)}
                />
              )}
            </Flex>
          )}
        </Card>

        {/* Pager (scale pass 2). Renders only when the filtered set spans
            more than one page, so a small queue looks exactly as before.
            `total` is the server's full filtered count from the paged
            envelope. */}
        {!loading && total > PAGE_SIZE && (
          <Flex
            sx={{
              marginTop: "14px",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "12px",
            }}
          >
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              {`${(page - 1) * PAGE_SIZE + 1}\u2013${Math.min(
                page * PAGE_SIZE,
                total,
              )} of ${total}`}
            </Subtext>
            <BasicButton
              type="button"
              outline
              scheme="secondary"
              styles={{ margin: "0" }}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </BasicButton>
            <BasicButton
              type="button"
              outline
              scheme="secondary"
              styles={{ margin: "0" }}
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </BasicButton>
          </Flex>
        )}
      </Page>

      {showNewWo && (
        <NewWorkOrderModal
          onClose={() => setShowNewWo(false)}
          onCreated={() => {
            setShowNewWo(false);
            load();
          }}
        />
      )}

      {matchTarget && (
        <MatchCandidatesModal
          workOrder={matchTarget}
          onClose={() => setMatchTarget(null)}
          onMatched={() => {
            setMatchTarget(null);
            load();
          }}
        />
      )}

      {assignTarget && (
        <BasicModal
          title="Assign to batch"
          cancel_click={() => {
            setAssignTarget(null);
            setAssignGroupId(null);
          }}
          cancel_text="Cancel"
          size="sm"
          confirm_click={submitAssign}
          confirm_text="Save"
          submitting={assigning}
        >
          <P>
            Work order{" "}
            <strong style={{ color: themevals.colors.black }}>
              {workOrderDisplayIdentity(assignTarget).primary}
            </strong>
            {assignTarget?.servicePlan?.name ? (
              <>
                {" on "}
                <strong style={{ color: themevals.colors.black }}>
                  {assignTarget.servicePlan.name}
                </strong>
              </>
            ) : null}
            .
          </P>
          <Box sx={{ marginTop: "12px" }}>
            <SelectInput
              name="groupId"
              label="Batch"
              value={assignGroupId ?? ""}
              options={[
                { value: "", label: "Unassigned" },
                ...(assignTarget?.servicePlan?.id
                  ? (groupsByPlan[assignTarget.servicePlan.id] || []).map((g: any) => ({
                      value: g.id,
                      label: g.name,
                    }))
                  : []),
              ]}
              onChange={(v: any) => setAssignGroupId(v || null)}
              searchable={false}
              clearable={false}
            />
          </Box>
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"10px"}
            color={themevals.colors.dkgrey}
          >
            Batches tag work orders, not Locations directly. A Location ends up in a batch when one
            of its WOs is in that batch.
          </Subtext>
        </BasicModal>
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  sub,
  valueTone,
}: {
  label: string;
  value: number;
  sub?: string;
  valueTone?: "amber" | "green";
}) {
  // Mockup 06: borderless beige card, calm. Urgency is carried by the
  // VALUE color (amber for unmatched, green for ready), never by tinting
  // the whole card.
  return (
    <Box sx={mntStatCard()}>
      <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>{label}</Box>
      <Box
        sx={{
          ...mntType.valueLg,
          marginTop: "4px",
          color:
            valueTone === "amber" ? mnt.amberDeep : valueTone === "green" ? mnt.green : mnt.ink,
        }}
      >
        {value}
      </Box>
      {sub && <Box sx={{ ...mntType.labelSm, marginTop: "2px" }}>{sub}</Box>}
    </Box>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        background: mnt.cardBg,
        border: "0.5px solid",
        borderColor: mnt.divider,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * THE REVIEW-QUEUE ROW, in two densities (Session 82).
 *
 * Same design as the plan roster and the plan work-order list: no open
 * record gives the list the full width and its rows earn extra columns;
 * an open record collapses the list and the row sheds them.
 *
 * This queue is CROSS-PLAN triage, so the client is a column the plan
 * lists do not need — a row here has to say whose work it is.
 */
function QueueListRow({
  wo,
  isLast,
  dense,
  isOpen,
  onOpen,
}: {
  wo: any;
  isLast: boolean;
  dense: boolean;
  isOpen: boolean;
  onOpen: () => void;
}) {
  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || null;
  const clientName = wo.lcp?.client?.name || wo.servicePlan?.client?.name || null;
  const label = wo.externalId || wo.name || "Client request";
  const reason = woUnresolvedReason(wo);
  const received = wo.receivedAt ? moment(wo.receivedAt).fromNow(true) : "";

  // Amber until it is resolved, blue once an RFQ exists. The dot is the
  // only state marker in the row; the words are in the pane.
  const dotColor = reason ? mnt.red : isPostRfq(wo) ? mnt.link : mnt.amber;

  return (
    <Flex
      onClick={onOpen}
      sx={{
        alignItems: "center",
        gap: dense ? "10px" : "12px",
        padding: dense ? "10px 14px" : "11px 16px",
        borderBottom: isLast ? "none" : "0.5px solid",
        borderColor: mnt.divider,
        background: isOpen ? mnt.bgBeige : "transparent",
        cursor: "pointer",
        "&:hover": { background: isOpen ? mnt.bgBeige : mnt.bgSubtle },
      }}
    >
      <Box sx={{ width: "7px", height: "7px", borderRadius: "4px", background: dotColor, flexShrink: 0 }} />

      <Box sx={{ flex: dense ? "1 1 auto" : "0 0 180px", minWidth: 0 }}>
        <Box
          as="div"
          sx={{
            fontFamily: "Poppins",
            fontSize: "12.5px",
            fontWeight: 500,
            color: mnt.link,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Box>
        {dense && (
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "10.5px",
              color: mnt.textFaint,
              marginTop: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {[clientName, siteName || reason].filter(Boolean).join(" \u00b7 ")}
          </Box>
        )}
      </Box>

      {!dense && (
        <>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "12px",
              width: "190px",
              flexShrink: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {clientName || ""}
          </Box>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "12px",
              color: siteName ? mnt.ink : mnt.red,
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {siteName ? [siteName, city].filter(Boolean).join(" \u00b7 ") : reason || "No matching site"}
          </Box>
        </>
      )}

      <Box
        as="div"
        sx={{
          fontFamily: "Poppins",
          fontSize: "11px",
          color: mnt.textMuted,
          width: dense ? "52px" : "64px",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {received}
      </Box>
    </Flex>
  );
}

function QueueListHeader() {
  const cell = { ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" };
  return (
    <Flex
      sx={{
        padding: "9px 16px",
        borderBottom: "0.5px solid",
        borderColor: mnt.divider,
        background: mnt.bgSubtle,
        gap: "12px",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "7px", flexShrink: 0 }} />
      <Box sx={{ ...cell, flex: "0 0 180px" }}>WORK ORDER</Box>
      <Box sx={{ ...cell, width: "190px", flexShrink: 0 }}>CLIENT</Box>
      <Box sx={{ ...cell, flex: "1 1 auto", minWidth: 0 }}>SITE</Box>
      <Box sx={{ ...cell, width: "64px", flexShrink: 0, textAlign: "right" }}>AGE</Box>
    </Flex>
  );
}

/**
 * The open work order, beside the queue. Carries what the row shed plus
 * the triage actions — matching a site is the whole job of this screen,
 * so it is the primary here.
 */
function QueuePane({
  wo,
  currentBatchName,
  onClose,
  onOpen,
  onMatch,
  onGenerateRfq,
  onAssignBatch,
  onViewRfq,
}: {
  wo: any;
  currentBatchName: string | null;
  onClose: () => void;
  onOpen: () => void;
  onMatch: () => void;
  onGenerateRfq: () => void;
  onAssignBatch: () => void;
  onViewRfq: (rfqId: string) => void;
}) {
  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || null;
  const clientName = wo.lcp?.client?.name || wo.servicePlan?.client?.name || null;
  const label = wo.externalId || wo.name || "Client request";
  const reason = woUnresolvedReason(wo);
  const canMatch = canMatchWo(wo);
  const canGenerate = canGenerateRfqForWo(wo);

  const secondary = {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: 500,
    padding: "8px 14px",
    borderRadius: "6px",
    background: mnt.cardBg,
    border: `1px solid ${mnt.divider}`,
    color: mnt.ink,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  };
  const primary = {
    ...secondary,
    background: mnt.ink,
    border: "none",
    color: mnt.cardBg,
  };

  return (
    <Box
      sx={{
        background: mnt.cardBg,
        border: "0.5px solid",
        borderColor: mnt.divider,
        borderRadius: "8px",
        flex: "1 1 auto",
        minWidth: 0,
        padding: "20px 22px",
      }}
    >
      <Flex sx={{ alignItems: "flex-start", gap: "14px", marginBottom: "16px" }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "17px", fontWeight: 600 }}>
            {label}
          </Box>
          <Box
            as="div"
            sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.textMuted, marginTop: "4px" }}
          >
            {[
              clientName,
              siteName ? [siteName, city].filter(Boolean).join(", ") : null,
              wo.servicePlan?.name || null,
              currentBatchName,
              wo.receivedAt ? "received " + moment(wo.receivedAt).format("MMM D") : null,
            ]
              .filter(Boolean)
              .join(" \u00b7 ")}
          </Box>
        </Box>
        <button type="button" onClick={onClose} style={{ ...secondary, padding: "6px 10px" }}>
          Close
        </button>
      </Flex>

      {/* THE UNRESOLVED CUE, stated rather than coloured. A row can only
          hint at this in red; here it can say what is actually wrong,
          which is what someone triaging needs to read. */}
      {reason && (
        <Box
          sx={{
            background: mnt.bgSubtle,
            border: `1px solid ${mnt.divider}`,
            borderRadius: "8px",
            padding: "12px 14px",
            marginBottom: "16px",
            fontFamily: "Poppins",
            fontSize: "12.5px",
            color: mnt.ink,
          }}
        >
          {reason}
        </Box>
      )}

      <Flex sx={{ gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
        {canMatch && (
          <button type="button" onClick={onMatch} style={primary}>
            Match to a site
          </button>
        )}
        {canGenerate && (
          <button type="button" onClick={onGenerateRfq} style={canMatch ? secondary : primary}>
            Generate RFQ
          </button>
        )}
        {wo.quoteRequestId && (
          <button type="button" onClick={() => onViewRfq(wo.quoteRequestId)} style={secondary}>
            View RFQ
          </button>
        )}
        {wo.servicePlan?.id && (
          <button type="button" onClick={onAssignBatch} style={secondary}>
            {currentBatchName ? "Change batch" : "Add to batch"}
          </button>
        )}
        <button type="button" onClick={onOpen} style={secondary}>
          Open work order
        </button>
      </Flex>

      {wo.problemDescription && (
        <Box>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px", marginBottom: "7px" }}>
            WHAT WAS ASKED FOR
          </Box>
          <Box
            as="div"
            sx={{
              fontFamily: "Poppins",
              fontSize: "12.5px",
              lineHeight: "19px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {wo.problemDescription}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function TableHeaderRow() {
  const cols = [
    { flex: "0 0 12%", label: "WO #" },
    { flex: "0 0 10%", label: "SOURCE" },
    { flex: "0 0 22%", label: "SITE" },
    { flex: "0 0 16%", label: "SERVICE · DUE" },
    { flex: "0 0 9%", label: "NTE" },
    { flex: "0 0 13%", label: "STATUS" },
    { flex: 1, label: "" },
  ];
  return (
    <Flex
      sx={{
        padding: "12px 20px",
        borderBottom: "0.5px solid",
        borderColor: mnt.divider,
        background: mnt.bgSubtle,
        gap: "10px",
        alignItems: "center",
      }}
    >
      {cols.map((c, i) => (
        <Box key={i} sx={{ flex: c.flex }}>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, letterSpacing: "0.3px" }}>{c.label}</Box>
        </Box>
      ))}
    </Flex>
  );
}

/* Row presentation (badge, source chip, SLA, aging, NTE, pills) lives in
 * ./components/workOrderPresentation so a WO reads identically in this
 * queue, in the plan-scoped list, and on the WO case file. Do not
 * re-add local copies here — that duplication is exactly what drifted. */

function WorkOrderRow({
  wo,
  isLast,
  planGroups,
  onMatch,
  onGenerateRfq,
  onAssignBatch,
  onViewRfq,
  onOpen,
}: {
  wo: any;
  isLast: boolean;
  planGroups: any[] | undefined;
  onMatch: () => void;
  onGenerateRfq: () => void;
  onAssignBatch: () => void;
  onViewRfq: (rfqId: string) => void;
  onOpen: () => void;
}) {
  const postRfq = isPostRfq(wo);
  const canMatch = canMatchWo(wo);
  const canGenerate = canGenerateRfqForWo(wo);

  const address = wo.lcp?.location?.address;
  const city = address ? [address.city, address.state].filter(Boolean).join(", ") : "";
  const planName = wo.servicePlan?.name || null;
  const siteName = wo.lcp?.name || wo.lcp?.location?.name || address?.line1 || null;
  // A1 — the global queue is cross-plan triage, so a row must say whose
  // work it is. Client is on the payload via lcp.client (preferred) with
  // the plan's client as fallback (both are joined in WorkOrderService.list).
  const clientName = wo.lcp?.client?.name || wo.servicePlan?.client?.name || null;

  // Unresolved cue (mockup 06). No LCP at all → we don't know the site
  // yet. LCP but no plan → known site, one-off.
  const reason = woUnresolvedReason(wo);

  const currentBatchName = (() => {
    if (!wo.groupId) return null;
    if (!planGroups) return null;
    const g = planGroups.find((x: any) => x.id === wo.groupId);
    return g?.name ?? null;
  })();
  const showBatchChip =
    !!wo.servicePlan?.id && Array.isArray(planGroups) && planGroups.length > 0;

  const lifecycle = lifecycleBadge(wo);
  const sla = slaCell(wo);
  const received = receivedCell(wo);
  // A12 — identity is number-first, name-secondary (or name-primary when
  // there is no number), resolved in one shared place so the queue, the
  // case file, and the context banner never disagree.
  const identity = workOrderDisplayIdentity(wo);

  return (
    <Flex
      sx={{
        alignItems: "flex-start",
        gap: "10px",
        padding: "14px 20px",
        borderBottom: isLast ? "none" : "0.5px solid",
        borderColor: mnt.divider,
        "&:hover": { background: mnt.bgSubtle },
      }}
    >
      {/* WO # + received */}
      <Box sx={{ flex: "0 0 12%", minWidth: 0 }}>
        <button
          type="button"
          onClick={onOpen}
          title="Open work order"
          style={{
            display: "block",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            background: "transparent",
            border: "none",
            padding: 0,
            textAlign: "left",
            cursor: "pointer",
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: "13px",
            color: themevals.colors.primary,
            textDecoration: "underline",
          }}
        >
          {identity.primary}
        </button>
        {identity.secondary && (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"2px"}
            color={themevals.colors.dkgrey}
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {identity.secondary}
          </Subtext>
        )}
        <Subtext
          fontSize={"11px"}
          fontFamily={"Poppins"}
          margin={0}
          marginTop={"2px"}
          sx={{ color: received.color, fontWeight: received.weight }}
        >
          {received.label}
        </Subtext>
      </Box>

      {/* Source (mockup 06: soft-blue CorrigoPro chip, grey Manual chip) */}
      <Box sx={{ flex: "0 0 10%", minWidth: 0 }}>
        <SourcePill sourceType={wo.sourceType} />
        {/* Map status — shown at every stage except rejected (see
         * showsMapStatus). Pre-pricing it tells an operator which WOs
         * still need a site measure before they can be priced; past the
         * RFQ the same chip reads as a fact about how the work was
         * priced, not a prompt. */}
        {showsMapStatus(wo) && (
          <Box sx={{ marginTop: "4px" }}>
            <MapStatusPill wo={wo} />
          </Box>
        )}
      </Box>

      {/* Site: client + name + city/plan, plus the unresolved cue */}
      <Box sx={{ flex: "0 0 22%", minWidth: 0 }}>
        {clientName && (
          <Span
            fontFamily={"Poppins"}
            fontSize={"12px"}
            fontWeight={600}
            color={themevals.colors.black}
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {clientName}
          </Span>
        )}
        {siteName ? (
          <>
            <Span
              fontFamily={"Poppins"}
              fontSize={"12px"}
              fontWeight={600}
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {siteName}
            </Span>
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              margin={0}
              color={themevals.colors.dkgrey}
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {[city, planName].filter(Boolean).join(" \u00b7 ")}
            </Subtext>
            {reason && (
              <Box sx={{ marginTop: "4px" }}>
                <UnresolvedPill reason={reason} />
              </Box>
            )}
          </>
        ) : (
          <UnresolvedPill reason={reason || "No matching site"} />
        )}
        {/* one-off + batch chips */}
        <Flex sx={{ gap: "6px", marginTop: "4px", flexWrap: "wrap" }}>
          {!planName && !!wo.lcp?.id && !postRfq && <OneOffPill />}
          {showBatchChip && (
            <button
              type="button"
              onClick={onAssignBatch}
              style={{
                background: currentBatchName ? mnt.bgBeige : "transparent",
                border: "1px dashed",
                borderColor: currentBatchName ? mnt.divider : mnt.textFaint,
                borderRadius: "9px",
                padding: "2px 8px",
                fontSize: "10.5px",
                fontFamily: "Poppins",
                color: currentBatchName ? mnt.ink : mnt.textMuted,
                cursor: "pointer",
              }}
            >
              {currentBatchName ? "Batch: " + currentBatchName : "+ Batch"}
            </button>
          )}
        </Flex>
      </Box>

      {/* Service · SLA */}
      <Box sx={{ flex: "0 0 16%", minWidth: 0 }}>
        <Span
          fontFamily={"Poppins"}
          fontSize={"12px"}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {wo.problemDescription || "-"}
        </Span>
        {sla && (
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            sx={{ color: sla.color, fontWeight: sla.weight }}
          >
            {sla.label}
          </Subtext>
        )}
        {wo.priority === "urgent" && (
          <Box sx={{ marginTop: "3px" }}>
            <UrgentPill />
          </Box>
        )}
      </Box>

      {/* NTE */}
      <Box sx={{ flex: "0 0 9%", minWidth: 0 }}>
        <Span fontFamily={"Poppins"} fontSize={"12px"} color={themevals.colors.black}>
          {formatNte(wo.nteAmount)}
        </Span>
      </Box>

      {/* Status */}
      <Box sx={{ flex: "0 0 13%", minWidth: 0 }}>
        <Badge as="span" variant={lifecycle.variant as any}>
          {lifecycle.label}
        </Badge>
        {lifecycle.hint && (
          <Subtext
            fontSize={"10.5px"}
            fontFamily={"Poppins"}
            margin={0}
            marginTop={"3px"}
            color={themevals.colors.dkgrey}
          >
            {lifecycle.hint}
          </Subtext>
        )}
      </Box>

      {/* Actions. Mockup 06 weighting: filled for the ready-to-go action,
          outlined for the one that needs a decision first. */}
      <Flex sx={{ flex: 1, justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" }}>
        {wo.status === "converted" && wo.convertedRfq?.id && (
          <BasicButton
            type="button"
            outline
            scheme="secondary"
            onClick={() => onViewRfq(wo.convertedRfq.id)}
          >
            View RFQ
          </BasicButton>
        )}
        {canMatch && (
          <BasicButton type="button" outline scheme="primary" onClick={onMatch}>
            Match
          </BasicButton>
        )}
        {canGenerate && (
          <BasicButton type="button" scheme="primary" onClick={onGenerateRfq}>
            Generate RFQ
          </BasicButton>
        )}
      </Flex>
    </Flex>
  );
}
